import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import './Scoreboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Scoreboard = () => {
  const [token, setToken] = useState(localStorage.getItem('scoreboard_token') || '');
  const [isSetup, setIsSetup] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [passcodeInput, setPasscodeInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(true);

  // Data
  const [summary, setSummary] = useState(null);
  const [trades, setTrades] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [spyData, setSpyData] = useState([]);
  const [sectorBreakdown, setSectorBreakdown] = useState([]);
  const [regimeBreakdown, setRegimeBreakdown] = useState([]);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [formData, setFormData] = useState({
    ticker: '', buy_date: '', buy_price: '', shares: '',
    sell_date: '', sell_price: '', market_regime: '', notes: ''
  });

  // Date range filter
  const [startDate, setStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Table sorting
  const [sortField, setSortField] = useState('buy_date');
  const [sortDirection, setSortDirection] = useState('desc');

  const headers = { 'Content-Type': 'application/json', 'X-Scoreboard-Token': token };

  // Check setup status
  useEffect(() => {
    fetch(`${API_URL}/api/scoreboard/check-setup`)
      .then(r => r.json())
      .then(d => { setIsSetup(d.is_setup); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Validate existing token
  useEffect(() => {
    if (token && isSetup) {
      fetch(`${API_URL}/api/scoreboard/trades`, { headers })
        .then(r => { if (r.ok) { setAuthenticated(true); } else { setToken(''); localStorage.removeItem('scoreboard_token'); } })
        .catch(() => { setToken(''); localStorage.removeItem('scoreboard_token'); });
    }
  }, [token, isSetup]);

  const fetchData = useCallback(async () => {
    if (!authenticated) return;
    try {
      const [summaryRes, spyRes] = await Promise.all([
        fetch(`${API_URL}/api/scoreboard/summary?start_date=${startDate}&end_date=${endDate}`, { headers }),
        fetch(`${API_URL}/api/scoreboard/spy-benchmark?start_date=${startDate}&end_date=${endDate}`, { headers })
      ]);
      const summaryData = await summaryRes.json();
      const spyResult = await spyRes.json();

      if (summaryData.status === 'success') {
        setSummary(summaryData.summary);
        setTrades(summaryData.trades);
        setChartData(summaryData.chart_data);
        setSectorBreakdown(summaryData.sector_breakdown);
        setRegimeBreakdown(summaryData.regime_breakdown);
      }
      if (spyResult.status === 'success') {
        setSpyData(spyResult.spy_data || []);
      }
    } catch (err) {
      console.error('Error fetching scoreboard data:', err);
    }
  }, [authenticated, startDate, endDate, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    const endpoint = isSetup ? '/api/scoreboard/auth' : '/api/scoreboard/setup-passcode';
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: passcodeInput })
      });
      const data = await res.json();
      if (res.ok && data.token) {
        setToken(data.token);
        localStorage.setItem('scoreboard_token', data.token);
        setAuthenticated(true);
        setPasscodeInput('');
        if (!isSetup) setIsSetup(true);
      } else {
        setAuthError(data.detail || 'Authentication failed');
      }
    } catch { setAuthError('Connection error'); }
  };

  const handleSubmitTrade = async (e) => {
    e.preventDefault();
    const url = editingTrade
      ? `${API_URL}/api/scoreboard/trades/${editingTrade.trade_id}`
      : `${API_URL}/api/scoreboard/trades`;
    const method = editingTrade ? 'PUT' : 'POST';

    try {
      const payload = { ...formData };
      if (!payload.sell_date) { payload.sell_date = null; payload.sell_price = null; }
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (res.ok) {
        resetForm();
        fetchData();
      }
    } catch (err) { console.error('Error saving trade:', err); }
  };

  const handleDeleteTrade = async (tradeId) => {
    if (!window.confirm('Delete this trade?')) return;
    try {
      await fetch(`${API_URL}/api/scoreboard/trades/${tradeId}`, { method: 'DELETE', headers });
      fetchData();
    } catch (err) { console.error('Error deleting trade:', err); }
  };

  const handleEditTrade = (trade) => {
    setEditingTrade(trade);
    setFormData({
      ticker: trade.ticker, buy_date: trade.buy_date, buy_price: trade.buy_price,
      shares: trade.shares, sell_date: trade.sell_date || '', sell_price: trade.sell_price || '',
      market_regime: trade.market_regime || '', notes: trade.notes || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ ticker: '', buy_date: '', buy_price: '', shares: '', sell_date: '', sell_price: '', market_regime: '', notes: '' });
    setEditingTrade(null);
    setShowForm(false);
  };

  const handleLogout = () => {
    setToken(''); setAuthenticated(false);
    localStorage.removeItem('scoreboard_token');
  };

  // Merge chart data with SPY data — carry forward portfolio ROI to create smooth connected line
  const getMergedChartData = () => {
    const dateMap = {};
    // Add all SPY dates first
    spyData.forEach(d => { dateMap[d.date] = { date: d.date, spy_return: d.spy_return }; });
    // Overlay portfolio data points
    chartData.forEach(d => {
      if (!dateMap[d.date]) dateMap[d.date] = { date: d.date };
      dateMap[d.date].portfolio_roi = d.cumulative_roi;
    });
    // Sort chronologically
    const sorted = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
    // Carry forward portfolio ROI to fill gaps (smooth connected line)
    let lastRoi = null;
    for (const point of sorted) {
      if (point.portfolio_roi !== undefined) {
        lastRoi = point.portfolio_roi;
      } else if (lastRoi !== null) {
        point.portfolio_roi = lastRoi;
      }
    }
    return sorted;
  };

  const SECTOR_COLORS = {
    'Energy': '#f59e0b', 'Technology': '#3b82f6', 'Health Care': '#10b981',
    'Financials': '#8b5cf6', 'Communication Services': '#ec4899', 'Industrials': '#6366f1',
    'Consumer Discretionary': '#14b8a6', 'Consumer Staples': '#84cc16', 'Utilities': '#f97316',
    'Real Estate': '#06b6d4', 'Materials': '#a855f7', 'Unknown': '#64748b'
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedTrades = () => {
    return [...trades].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      // Handle numeric fields
      if (['buy_price', 'sell_price', 'shares', 'return_pct', 'return_dollar'].includes(sortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      // Handle string/date fields
      if (typeof aVal === 'string') aVal = aVal || '';
      if (typeof bVal === 'string') bVal = bVal || '';
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortHeader = ({ field, label }) => (
    <th onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {sortField === field ? (sortDirection === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
    </th>
  );

  if (loading) return <div className="scoreboard-container"><div className="scoreboard-loading">Loading...</div></div>;

  // Auth gate
  if (!authenticated) {
    return (
      <div className="scoreboard-container">
        <div className="auth-gate" data-testid="scoreboard-auth-gate">
          <div className="auth-card">
            <h1 className="auth-title">Scoreboard</h1>
            <p className="auth-subtitle">Pro Stock Money BOWL</p>
            <p className="auth-description">
              {isSetup ? 'Enter your passcode to access the Scoreboard.' : 'Set up your Scoreboard passcode.'}
            </p>
            <form onSubmit={handleAuth} className="auth-form">
              <input
                data-testid="scoreboard-passcode-input"
                type="password"
                value={passcodeInput}
                onChange={e => setPasscodeInput(e.target.value)}
                placeholder={isSetup ? 'Enter passcode' : 'Create passcode (min 4 chars)'}
                className="auth-input"
                autoFocus
              />
              {authError && <p className="auth-error" data-testid="scoreboard-auth-error">{authError}</p>}
              <button type="submit" className="auth-button" data-testid="scoreboard-auth-submit">
                {isSetup ? 'Unlock' : 'Set Passcode'}
              </button>
            </form>
            <a href="/explore" className="back-link">Back to Dashboard</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="scoreboard-container" data-testid="scoreboard-page">
      {/* Header */}
      <div className="scoreboard-header">
        <div className="header-top-row">
          <a href="/explore" className="back-link">Back to Dashboard</a>
          <button onClick={handleLogout} className="logout-btn" data-testid="scoreboard-logout">Lock</button>
        </div>
        <h1>Scoreboard</h1>
        <p className="scoreboard-subtitle">Pro Stock Money BOWL - Trade Performance Tracker</p>
      </div>

      {/* Date Range Filter */}
      <div className="date-filter" data-testid="scoreboard-date-filter">
        <label>From:</label>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="date-input" data-testid="scoreboard-start-date" />
        <label>To:</label>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="date-input" data-testid="scoreboard-end-date" />
        <button onClick={fetchData} className="filter-btn" data-testid="scoreboard-apply-filter">Apply</button>
        <button onClick={() => { setStartDate(`${new Date().getFullYear()}-01-01`); setEndDate(new Date().toISOString().split('T')[0]); }} className="filter-btn secondary" data-testid="scoreboard-reset-ytd">YTD</button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="stats-row" data-testid="scoreboard-summary-stats">
          <div className="stat-card">
            <span className="stat-label">Total ROI</span>
            <span className={`stat-value ${summary.total_return_pct >= 0 ? 'positive' : 'negative'}`} data-testid="scoreboard-total-roi">
              {summary.total_return_pct >= 0 ? '+' : ''}{summary.total_return_pct}%
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Total Return</span>
            <span className={`stat-value ${summary.total_return_dollar >= 0 ? 'positive' : 'negative'}`}>
              ${summary.total_return_dollar.toLocaleString()}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Win Rate</span>
            <span className="stat-value" data-testid="scoreboard-win-rate">{summary.win_rate}%</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Avg Gain</span>
            <span className="stat-value positive">+{summary.avg_gain_pct}%</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Avg Loss</span>
            <span className="stat-value negative">{summary.avg_loss_pct}%</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Trades</span>
            <span className="stat-value">{summary.total_trades} ({summary.open_trades} open)</span>
          </div>
        </div>
      )}

      {/* Cumulative ROI Chart */}
      <div className="chart-section" data-testid="scoreboard-chart">
        <h2>Cumulative Return vs S&P 500</h2>
        {getMergedChartData().length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={getMergedChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <YAxis stroke="#94a3b8" tickFormatter={v => `${v}%`} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#f8fafc' }}
                formatter={(value, name) => [`${value}%`, name]}
              />
              <Legend />
              <Line type="monotone" dataKey="portfolio_roi" name="Portfolio" stroke="#8b5cf6" strokeWidth={2.5} dot={false} connectNulls />
              <Line type="monotone" dataKey="spy_return" name="S&P 500" stroke="#64748b" strokeWidth={1.5} dot={false} strokeDasharray="5 5" connectNulls />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="empty-chart">Add trades to see your performance chart</div>
        )}
      </div>

      {/* Sector + Regime Breakdown */}
      {(sectorBreakdown.length > 0 || regimeBreakdown.length > 0) && (
        <div className="breakdown-row">
          {sectorBreakdown.length > 0 && (
            <div className="breakdown-card" data-testid="scoreboard-sector-breakdown">
              <h3>By Sector</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={sectorBreakdown} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="sector" stroke="#94a3b8" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={60} />
                  <YAxis stroke="#94a3b8" tickFormatter={v => `${v}%`} />
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#f8fafc' }} formatter={v => [`${v}%`, 'Return']} />
                  <Bar dataKey="return_pct" name="Return %">
                    {sectorBreakdown.map((entry, i) => (
                      <Cell key={i} fill={SECTOR_COLORS[entry.sector] || '#64748b'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {regimeBreakdown.length > 0 && (
            <div className="breakdown-card" data-testid="scoreboard-regime-breakdown">
              <h3>By Market Regime</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={regimeBreakdown} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="regime" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#94a3b8" tickFormatter={v => `${v}%`} />
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#f8fafc' }} formatter={v => [`${v}%`, 'Return']} />
                  <Bar dataKey="return_pct" name="Return %">
                    {regimeBreakdown.map((entry, i) => (
                      <Cell key={i} fill={entry.regime === 'shock' ? '#ef4444' : entry.regime === 'normal' ? '#22c55e' : '#64748b'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Add Trade Button */}
      <div className="trade-actions">
        <button onClick={() => { resetForm(); setShowForm(true); }} className="add-trade-btn" data-testid="scoreboard-add-trade-btn">
          + Add Trade
        </button>
      </div>

      {/* Trade Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="trade-form-modal" onClick={e => e.stopPropagation()} data-testid="scoreboard-trade-form">
            <h3>{editingTrade ? 'Edit Trade' : 'Add New Trade'}</h3>
            <form onSubmit={handleSubmitTrade}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Ticker</label>
                  <input data-testid="trade-ticker" type="text" value={formData.ticker} onChange={e => setFormData({ ...formData, ticker: e.target.value })} placeholder="AAPL" required />
                </div>
                <div className="form-group">
                  <label>Buy Date</label>
                  <input data-testid="trade-buy-date" type="date" value={formData.buy_date} onChange={e => setFormData({ ...formData, buy_date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Buy Price</label>
                  <input data-testid="trade-buy-price" type="number" step="0.01" value={formData.buy_price} onChange={e => setFormData({ ...formData, buy_price: e.target.value })} placeholder="150.00" required />
                </div>
                <div className="form-group">
                  <label>Shares</label>
                  <input data-testid="trade-shares" type="number" step="0.01" value={formData.shares} onChange={e => setFormData({ ...formData, shares: e.target.value })} placeholder="10" required />
                </div>
                <div className="form-group">
                  <label>Sell Date (leave empty if open)</label>
                  <input data-testid="trade-sell-date" type="date" value={formData.sell_date} onChange={e => setFormData({ ...formData, sell_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Sell Price</label>
                  <input data-testid="trade-sell-price" type="number" step="0.01" value={formData.sell_price} onChange={e => setFormData({ ...formData, sell_price: e.target.value })} placeholder="165.00" />
                </div>
                <div className="form-group">
                  <label>Market Regime</label>
                  <select data-testid="trade-regime" value={formData.market_regime} onChange={e => setFormData({ ...formData, market_regime: e.target.value })}>
                    <option value="">Select...</option>
                    <option value="normal">Normal</option>
                    <option value="shock">Shock / Risk-Off</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input data-testid="trade-notes" type="text" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional notes" />
                </div>
              </div>
              <div className="form-buttons">
                <button type="submit" className="submit-btn" data-testid="trade-submit-btn">{editingTrade ? 'Update' : 'Add Trade'}</button>
                <button type="button" onClick={resetForm} className="cancel-btn">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Trade Table */}
      <div className="trades-table-section" data-testid="scoreboard-trades-table">
        <h2>All Trades</h2>
        {trades.length > 0 ? (
          <div className="table-wrapper">
            <table className="trades-table">
              <thead>
                <tr>
                  <SortHeader field="status" label="Status" />
                  <SortHeader field="ticker" label="Ticker" />
                  <SortHeader field="sector" label="Sector" />
                  <SortHeader field="buy_date" label="Buy Date" />
                  <SortHeader field="buy_price" label="Buy Price" />
                  <SortHeader field="shares" label="Shares" />
                  <SortHeader field="sell_date" label="Sell Date" />
                  <SortHeader field="sell_price" label="Sell Price" />
                  <SortHeader field="return_pct" label="Return %" />
                  <SortHeader field="return_dollar" label="Return $" />
                  <SortHeader field="market_regime" label="Regime" />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getSortedTrades().map(trade => (
                  <tr key={trade.trade_id} className={trade.status === 'open' ? 'open-trade' : ''} data-testid={`trade-row-${trade.trade_id}`}>
                    <td><span className={`status-badge ${trade.status}`}>{trade.status}</span></td>
                    <td className="ticker-cell">{trade.ticker}</td>
                    <td>{trade.sector}</td>
                    <td>{trade.buy_date}</td>
                    <td>${trade.buy_price?.toFixed(2)}</td>
                    <td>{trade.shares}</td>
                    <td>{trade.sell_date || '—'}</td>
                    <td>{trade.sell_price ? `$${trade.sell_price.toFixed(2)}` : trade.current_price ? `$${trade.current_price.toFixed(2)} (live)` : '—'}</td>
                    <td className={trade.return_pct >= 0 ? 'positive' : 'negative'}>
                      {trade.return_pct >= 0 ? '+' : ''}{trade.return_pct}%
                    </td>
                    <td className={trade.return_dollar >= 0 ? 'positive' : 'negative'}>
                      ${trade.return_dollar?.toLocaleString()}
                    </td>
                    <td><span className={`regime-tag ${trade.market_regime || 'none'}`}>{trade.market_regime || '—'}</span></td>
                    <td className="action-cell">
                      <button onClick={() => handleEditTrade(trade)} className="edit-btn" data-testid={`edit-trade-${trade.trade_id}`}>Edit</button>
                      <button onClick={() => handleDeleteTrade(trade.trade_id)} className="delete-btn" data-testid={`delete-trade-${trade.trade_id}`}>Del</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-table">No trades yet. Click "+ Add Trade" to get started.</div>
        )}
      </div>
    </div>
  );
};

export default Scoreboard;
